import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { extractRagField } from '../../../../api/document-rag/document-rag.api';
import type {
  ExtractedValueItemDTO,
  ExtractRagFieldApiResponse,
} from '../../../../api/document-rag/document-rag.api.types';
import { useAPIErrorHandler } from '../../../../components/hooks/useAPIErrorHandler';
import { getData } from '../../../../utils/functional/fp';

/**
 * Get all extracted values from the API response
 * Returns recommended_value first (if exists), then all other extracted_value items
 * Deduplicates based on id field to prevent duplicates
 * Returns array of DTOs - UI components should transform these to ExtractionResultItem
 */
function getAllExtractions(
  rawResponse: ExtractRagFieldApiResponse
): ExtractedValueItemDTO[] {
  const recommendedValue = rawResponse.recommended_value;
  const extractedValues = rawResponse.extracted_value || [];

  // Track seen ids to prevent duplicates
  const seenIds = new Set<string>();
  const deduplicated: ExtractedValueItemDTO[] = [];

  // Add recommended_value first if it exists
  if (recommendedValue) {
    if (recommendedValue.id) {
      seenIds.add(recommendedValue.id);
    }
    deduplicated.push(recommendedValue);
  }

  // Add extracted_value items, skipping those with ids that match recommended_value
  for (const value of extractedValues) {
    if (value.id) {
      // Skip if we've already seen this id (from recommended_value or previous extracted_value)
      if (!seenIds.has(value.id)) {
        seenIds.add(value.id);
        deduplicated.push(value);
      }
    } else {
      // If no id, include the value (backward compatibility)
      deduplicated.push(value);
    }
  }

  return deduplicated;
}

/**
 * Parameters for the useExtractFieldValue hook
 */
export interface UseExtractFieldValueParams {
  /** Semantic field name to extract (e.g., 'companyName', 'revenue') - mutually exclusive with fieldId */
  field?: string;
  /** Dynamic form field ID (UUID) - mutually exclusive with field */
  fieldId?: string;
  /** Message ID associated with the submission */
  messageId: string;
  /** Submission ID */
  submissionId: string;
  /** Maximum number of results to return (optional) */
  maxResults?: number;
  /** Optional conversation ID for maintaining context across extraction requests */
  conversationId?: string;
  /** Whether the query should be enabled (optional, defaults to true when all required params are present) */
  enabled?: boolean;
}

/**
 * Return type for useExtractFieldValue hook
 */
export interface UseExtractFieldValueReturn {
  /** All extracted value items (DTOs from API) - recommended_value first, then others */
  data: ExtractedValueItemDTO[];
  /** Raw API response */
  apiResponse: ExtractRagFieldApiResponse | undefined;
  /** Conversation ID from API response (if provided) */
  conversationId: string | undefined;
  /** Whether the query is loading */
  isLoading: boolean;
  /** Whether the query has an error */
  isError: boolean;
  /** Function to refetch the data */
  refetch: () => void;
  /** Timestamp when data was last updated (for cache checking) */
  dataUpdatedAt: number | undefined;
}

/**
 * React Query hook for extracting field values from submission documents using RAG
 *
 * @example
 * ```typescript
 * const { data, apiResponse, conversationId, isLoading, isError } = useExtractFieldValue({
 *   field: 'companyName',
 *   messageId: 'msg-123',
 *   submissionId: 'sub-456',
 *   conversationId: 'conv-789',
 *   maxResults: 5
 * });
 * ```
 */
export function useExtractFieldValue({
  field,
  fieldId,
  messageId,
  submissionId,
  maxResults,
  conversationId,
  enabled,
}: UseExtractFieldValueParams) {
  const handleAPIError = useAPIErrorHandler();

  // Validate field identifiers: exactly one of field or fieldId must be provided (mutually exclusive)
  const hasBothIdentifiers = Boolean(field && fieldId);
  const hasFieldIdentifier = Boolean(field || fieldId);

  if (hasBothIdentifiers && process.env.NODE_ENV === 'development') {
    console.warn(
      'useExtractFieldValue: Both "field" and "fieldId" provided. These are mutually exclusive - only one should be used.'
    );
  }

  const isEnabled =
    enabled !== undefined
      ? enabled
      : Boolean(
          hasFieldIdentifier && !hasBothIdentifiers && messageId && submissionId
        );

  // Query key excludes conversationId to prevent cache invalidation when conversationId updates
  // conversationId is still passed to the API in queryFn for context
  const query = useQuery<ExtractRagFieldApiResponse>({
    queryKey: [
      'rag-extract',
      {
        // Include whichever field identifier is provided
        ...(field && { field }),
        ...(fieldId && { fieldId }),
        messageId,
        submissionId,
        maxResults,
        // conversationId removed from query key to prevent cache invalidation
        // when conversationId updates from API response
      },
    ],
    queryFn: async () => {
      const response = await extractRagField({
        params: {
          // Pass whichever field identifier is provided (mutually exclusive)
          ...(field && { field }),
          ...(fieldId && { fieldId }),
          submissionId,
          ...(maxResults !== undefined && { maxResults }),
          ...(conversationId !== undefined && { conversationId }),
        },
      });

      const rawData = getData(
        response
      ) as unknown as ExtractRagFieldApiResponse;

      return rawData;
    },
    onError: handleAPIError(
      'Unable to extract field value from documents. Please try again.'
    ),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - use cached data for 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
  });

  // Get all extractions from the API response
  // Memoize to prevent creating new object references on every render
  const allExtractions = useMemo(() => {
    if (!query.data) {
      return [];
    }

    return getAllExtractions(query.data as ExtractRagFieldApiResponse);
  }, [query.data]);

  // Extract conversationId from API response if available
  const responseConversationId = (
    query.data as ExtractRagFieldApiResponse | undefined
  )?.conversationId;

  return {
    data: allExtractions,
    apiResponse: query.data,
    conversationId: responseConversationId,
    isLoading: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
  };
}
